import {
  formatBirthCoordinatesLine,
  formatBirthTimezoneLine,
} from '@/utils/locationDisplay';
import {
  aspectMotionLabel as aspectMotionLabelCompact,
  formatAngleLabel as formatAngleLabelWithSettings,
  formatAspectLabel as formatAspectLabelWithSettings,
  formatNumericValue,
  formatPlanetLabel as formatPlanetLabelWithSettings,
  formatSignLabel as formatSignLabelWithSettings,
  formatZodiacPosition,
} from '@/utils/chartFormatters';
import { DEFAULT_CHART_DISPLAY_SETTINGS } from '@/utils/chartDisplaySettings';

export function safeAspects(aspects) {
  return Array.isArray(aspects) ? aspects : [];
}

export function symbolOrName(symbol, name) {
  return symbol || name || '';
}

/**
 * @deprecated Prefer formatPlanetLabel from chartFormatters with settings.
 * Kept for callers that pass { compact } options.
 */
export function formatPlanetLabel(planet, options = {}) {
  if (options && ('planetNameMode' in options || 'degreeFormat' in options)) {
    return formatPlanetLabelWithSettings(planet, options);
  }
  if (options?.compact) {
    return formatPlanetLabelWithSettings(planet, {
      ...DEFAULT_CHART_DISPLAY_SETTINGS,
      planetNameMode: 'none',
    });
  }
  return formatPlanetLabelWithSettings(planet, DEFAULT_CHART_DISPLAY_SETTINGS);
}

export function formatSignLabel(record, settings) {
  if (settings) {
    return formatSignLabelWithSettings(record, settings);
  }
  return formatSignLabelWithSettings(record, DEFAULT_CHART_DISPLAY_SETTINGS);
}

export function formatAngleLabel(angle, options = {}) {
  if (options && ('planetNameMode' in options || 'degreeFormat' in options)) {
    return formatAngleLabelWithSettings(angle, options);
  }
  if (options?.compact) {
    return formatAngleLabelWithSettings(angle, {
      ...DEFAULT_CHART_DISPLAY_SETTINGS,
      planetNameMode: 'none',
    });
  }
  return formatAngleLabelWithSettings(angle, DEFAULT_CHART_DISPLAY_SETTINGS);
}

export function formatAspectLabel(aspect, options = {}) {
  if (options && ('aspectNameMode' in options || 'planetNameMode' in options)) {
    return formatAspectLabelWithSettings(aspect, options);
  }
  if (options?.compact) {
    return formatAspectLabelWithSettings(aspect, {
      ...DEFAULT_CHART_DISPLAY_SETTINGS,
      planetNameMode: 'none',
      aspectNameMode: 'none',
    });
  }
  return formatAspectLabelWithSettings(aspect, DEFAULT_CHART_DISPLAY_SETTINGS);
}

export function aspectTitle(aspect) {
  if (!aspect) {
    return '';
  }
  return `${aspect.point_a || ''} ${aspect.aspect || ''} ${aspect.point_b || ''}`.trim();
}

export function formatOrb(aspect, settings) {
  let orbValue = aspect?.orb;
  if (orbValue == null || !Number.isFinite(Number(orbValue))) {
    const deg = Number(aspect?.orb_degrees ?? 0);
    const min = Number(aspect?.orb_minutes ?? 0);
    orbValue = deg + (min / 60);
  }
  const formatted = formatNumericValue(orbValue, settings);
  return formatted === '' ? '' : `${formatted}°`;
}

export function aspectMotionLabel(aspect) {
  return aspectMotionLabelCompact(aspect);
}

export function formatAspectRow(aspect, options = {}) {
  return formatAspectLabel(aspect, options);
}

export { formatZodiacPosition, formatNumericValue };

export function formatBirthName(birthData) {
  if (!birthData) {
    return '';
  }
  return [birthData.firstName, birthData.lastName].filter(Boolean).join(' ');
}

export function formatBirthLocation(birthData) {
  if (!birthData) {
    return '';
  }
  return [birthData.city, birthData.region, birthData.country].filter(Boolean).join(', ');
}

export function formatBirthCoordinates(birthData) {
  return formatBirthCoordinatesLine(birthData);
}

export function formatBirthTimezone(birthData) {
  return formatBirthTimezoneLine(birthData);
}

export function formatBirthDatetime(birthData) {
  if (!birthData) {
    return '';
  }
  const { year, month, day, hour, minute } = birthData;
  if (year == null || month == null || day == null) {
    return '';
  }

  const hourValue = hour ?? 0;
  const minuteValue = minute ?? 0;
  const date = new Date(year, month - 1, day, hourValue, minuteValue);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Format an ISO "YYYY-MM-DD" date string as a local calendar date, without
 * constructing a Date object -- new Date("YYYY-MM-DD") parses as UTC
 * midnight and can shift the displayed calendar day in some time zones.
 */
export function formatBirthDate(isoDate) {
  if (!isoDate) {
    return '';
  }
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) {
    return '';
  }
  return `${MONTH_NAMES[m - 1]} ${d}, ${y}`;
}

/**
 * Format an ISO "HH:MM" or "HH:MM:SS" time string as the submitted local
 * civil time. Never converts through the viewer's time zone.
 */
export function formatBirthTime(isoTime) {
  if (!isoTime) {
    return '';
  }
  const [hStr, mStr] = isoTime.split(':');
  let h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return '';
  }
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')} ${period}`;
}

export function birthLocationForAngles(birthData) {
  const name = formatBirthLocation(birthData);
  if (!name) {
    return null;
  }
  return {
    name,
    latitude: birthData.lat ?? null,
    longitude: birthData.long ?? null,
    timezone: birthData.timezone ?? null,
    city: birthData.city ?? '',
    region: birthData.region ?? '',
    country: birthData.country ?? '',
    country_code: birthData.country_code ?? '',
  };
}
